import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { ClassNavigation } from '../components/layout';
import { SurveySummary } from '../components/survey/SurveySummary';
import type { CategoryScore, BackgroundResponse } from '../types/survey-student';

interface Student {
  id: string;
  name: string;
  email: string;
}

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
}

const SurveyResults = () => {
  const { classId, studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [student, setStudent] = useState<Student | null>(null);
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [surveyData, setSurveyData] = useState<{
    scores: CategoryScore[];
    backgroundData: BackgroundResponse;
    hasResults: boolean;
    dataSource?: string;
  } | null>(null);

  useEffect(() => {
    const loadSurveyData = async () => {
      if (!classId || !studentId || !user) return;

      setLoading(true);
      try {
        // Load class info
        const { data: classData } = await apiService.getClassById(classId);
        if (classData) {
          setClassInfo({
            id: classData.id,
            name: classData.name,
            code: classData.code,
            term: classData.term,
          });
        }

        // Load student profile data
        const { data: profileData } = await apiService.getUserProfile(studentId);
        // Fetch student profile without classId to ensure consistency across classes
        const { data: studentProfileData } = await apiService.getStudentProfile(studentId);
        
        if (profileData) {
          setStudent({
            id: profileData.id,
            name: profileData.fullName || 'Unknown Student',
            email: profileData.email,
          });
        }

        // Extract background data
        const extractedBackground = extractBackgroundFromProfile(studentProfileData);

        // Load survey results
        const { data: surveyResults } = await apiService.getStudentSurveyResults(studentId);
        
        if (surveyResults && surveyResults.hasResults && surveyResults.categories?.length > 0) {
          setSurveyData({
            scores: surveyResults.categories,
            backgroundData: extractedBackground || {},
            hasResults: true,
            dataSource: surveyResults.dataSource || 'backend'
          });
        }

      } catch (error) {
        console.error('Failed to load survey data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSurveyData();
  }, [classId, studentId, user]);

  const extractBackgroundFromProfile = (studentProfile: any): BackgroundResponse => {
    if (!studentProfile?.backgroundData && !studentProfile?.responses) {
      return {};
    }

    // Check backgroundData first (from ProfileUpdate), then fall back to responses (legacy)
    const data = studentProfile.backgroundData || studentProfile.responses || {};
    
    return {
      name: data.name || studentProfile.fullName || '',
      email: data.email || studentProfile.email || '',
      classLevel: data.classLevel || '',
      hometown: data.hometown || '',
      classCount: data.classCount || '',
      majorCount: data.majorCount || '',
      major: data.major || data.majors || '',
      firstLanguage: data.firstLanguage || '',
      dailyLanguage: data.dailyLanguage || '',
      englishProficiency: data.englishProficiency || 0,
      speakingComfort: data.speakingComfort || 0,
      listeningSkill: data.listeningSkill || 0,
      readingComfort: data.readingComfort || 0,
      writingAbility: data.writingAbility || 0,
      memorizing: data.courseworkEmphasis?.memorizing || data.memorizing || '',
      applying: data.courseworkEmphasis?.applying || data.applying || '',
      analyzing: data.courseworkEmphasis?.analyzing || data.analyzing || '',
      evaluating: data.courseworkEmphasis?.evaluating || data.evaluating || '',
      forming: data.courseworkEmphasis?.forming || data.forming || '',
      numericalAnalysis: data.numericalTasks?.numericalAnalysis || data.numericalAnalysis || '',
      numericalProblems: data.numericalTasks?.numericalProblems || data.numericalProblems || '',
      evaluatingNumerical: data.numericalTasks?.evaluatingNumerical || data.evaluatingNumerical || '',
      papers5Pages: data.writingTasks?.papers5Pages || data.papers5Pages || '',
      papers6to10Pages: data.writingTasks?.papers6to10Pages || data.papers6to10Pages || '',
      papers11Plus: data.writingTasks?.papers11Plus || data.papers11Plus || '',
      previousCourses: data.previousCourses || '',
      reasonsForTaking: data.reasonsForTaking || [],
      preferredCommunication: data.preferredCommunication || ''
    };
  };

  const handleBack = () => {
    navigate(`/dashboard/class/${classId}/student/${studentId}`);
  };

  if (loading) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!student || !classInfo) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900">Student not found</h2>
            <p className="text-gray-600 mt-2">Unable to load survey results.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Student Details
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {student.name}
            </h1>
            <p className="text-gray-600">{classInfo.name} • {classInfo.code}</p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Survey Results Available</h3>
            <p className="text-gray-600">
              This student hasn't completed their survey yet or the results are not available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleRestart = () => {
    // For professor view, we don't allow restarting, but we can provide a message
    console.log('Restart not available in professor view');
  };

  return (
    <div>
      <ClassNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Student Context Header */}
        <div className="mb-6 bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                title="Back to Student Details"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {student.name}
                </h1>
                <p className="text-gray-600">{classInfo.name} • {classInfo.code}</p>
              </div>
            </div>
            {surveyData.dataSource === 'profile-based' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-sm font-medium text-amber-800">
                  Profile-Based Estimates
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Survey Summary - Direct component */}
        <div className="bg-white rounded-xl shadow-lg">
          <SurveySummary
            scores={surveyData.scores}
            backgroundData={surveyData.backgroundData}
            onRestart={handleRestart}
          />
        </div>
      </div>
    </div>
  );
};

export default SurveyResults;